<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import flowDefinitions from 'dashboard/api/flowDefinitions';
import FlowDefinitionTimeline from './FlowDefinitionTimeline.vue';
import FlowStepsList from './FlowStepsList.vue';
import { createDefaultDefinition } from './flowDefinitionSync';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const loading = ref(true);
const saving = ref(false);
const name = ref('New flow');
const active = ref(true);
const definition = ref(createDefaultDefinition());
const jsonText = ref(JSON.stringify(definition.value, null, 2));
const parseError = ref('');
const editorTab = ref('builder');

const flowId = computed(() => route.params.flowId);
const isNew = computed(() => flowId.value === 'new');

const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');

const parsedDefinition = computed(() => definition.value);

const saveBlocked = computed(
  () => editorTab.value === 'json' && !!parseError.value
);

const setEditorTab = tab => {
  if (tab === 'builder' && editorTab.value === 'json') {
    try {
      definition.value = JSON.parse(jsonText.value);
      parseError.value = '';
    } catch (e) {
      parseError.value = e.message;
      useAlert(t('FLOWS.INVALID_JSON'));
      return;
    }
  }
  if (tab === 'json') {
    jsonText.value = JSON.stringify(definition.value, null, 2);
    parseError.value = '';
  }
  editorTab.value = tab;
};

watch(jsonText, () => {
  if (editorTab.value !== 'json') return;
  try {
    JSON.parse(jsonText.value);
    parseError.value = '';
  } catch (e) {
    parseError.value = e.message;
  }
});

watch(
  definition,
  () => {
    if (editorTab.value === 'json') return;
    jsonText.value = JSON.stringify(definition.value, null, 2);
  },
  { deep: true }
);

onMounted(async () => {
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');

  if (isNew.value) {
    definition.value = createDefaultDefinition();
    jsonText.value = JSON.stringify(definition.value, null, 2);
    loading.value = false;
    return;
  }
  try {
    const { data } = await flowDefinitions.show(flowId.value);
    const row = data.payload;
    name.value = row.name;
    active.value = row.active;
    definition.value = row.definition || createDefaultDefinition();
    jsonText.value = JSON.stringify(definition.value, null, 2);
  } catch (e) {
    useAlert(t('FLOWS.LOAD_ERROR'));
    router.push({ name: 'settings_flows_index' });
  } finally {
    loading.value = false;
  }
});

const save = async () => {
  let defn = definition.value;
  if (editorTab.value === 'json') {
    try {
      defn = JSON.parse(jsonText.value);
      definition.value = defn;
    } catch (e) {
      useAlert(t('FLOWS.INVALID_JSON'));
      return;
    }
  }
  saving.value = true;
  try {
    if (isNew.value) {
      const { data } = await flowDefinitions.create({
        name: name.value,
        active: active.value,
        definition: defn,
      });
      useAlert(t('FLOWS.SAVE_SUCCESS'));
      router.replace({
        name: 'settings_flows_edit',
        params: { flowId: String(data.payload.id) },
      });
    } else {
      await flowDefinitions.update(flowId.value, {
        name: name.value,
        active: active.value,
        definition: defn,
      });
      useAlert(t('FLOWS.SAVE_SUCCESS'));
    }
  } catch (e) {
    useAlert(t('FLOWS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const tabClass = current =>
  editorTab.value === current
    ? 'bg-n-solid-2 text-n-slate-12 shadow-sm ring-1 ring-n-weak'
    : 'text-n-slate-11 hover:text-n-slate-12';

const nodeReferenceCards = computed(() => [
  {
    icon: 'i-lucide-play',
    title: t('FLOWS.NODE_TYPE_START'),
    desc: t('FLOWS.NODE_DESC_START'),
  },
  {
    icon: 'i-lucide-message-square-text',
    title: t('FLOWS.NODE_TYPE_MESSAGE'),
    desc: t('FLOWS.NODE_DESC_MESSAGE'),
  },
  {
    icon: 'i-lucide-git-branch',
    title: t('FLOWS.NODE_TYPE_WAIT_INPUT'),
    desc: t('FLOWS.NODE_DESC_WAIT_INPUT'),
  },
  {
    icon: 'i-lucide-split',
    title: t('FLOWS.NODE_TYPE_BRANCH'),
    desc: t('FLOWS.NODE_DESC_BRANCH'),
  },
  {
    icon: 'i-lucide-tag',
    title: t('FLOWS.NODE_TYPE_ADD_LABEL'),
    desc: t('FLOWS.NODE_DESC_ADD_LABEL'),
  },
  {
    icon: 'i-lucide-users',
    title: t('FLOWS.NODE_TYPE_ASSIGN_TEAM'),
    desc: t('FLOWS.NODE_DESC_ASSIGN_TEAM'),
  },
  {
    icon: 'i-lucide-message-circle',
    title: t('FLOWS.NODE_TYPE_WHATSAPP_TEMPLATE'),
    desc: t('FLOWS.NODE_DESC_WHATSAPP_TEMPLATE'),
  },
  {
    icon: 'i-lucide-flag',
    title: t('FLOWS.NODE_TYPE_END'),
    desc: t('FLOWS.NODE_DESC_END'),
  },
]);
</script>

<template>
  <SettingsLayout :is-loading="loading" :loading-message="$t('FLOWS.LOADING')">
    <template #header>
      <BaseSettingsHeader
        :title="isNew ? $t('FLOWS.NEW_TITLE') : $t('FLOWS.EDIT_TITLE')"
        :description="$t('FLOWS.EDIT_DESCRIPTION')"
      >
        <template #actions>
          <div class="flex flex-wrap items-center gap-2">
            <Button
              variant="faded"
              size="sm"
              :label="$t('FLOWS.BACK')"
              @click="router.push({ name: 'settings_flows_index' })"
            />
            <Button
              size="sm"
              :label="$t('FLOWS.SAVE')"
              :is-loading="saving"
              :disabled="saveBlocked"
              @click="save"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="mx-8 flex max-w-6xl flex-col gap-6 pb-16">
        <CardLayout layout="col">
          <div
            class="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between"
          >
            <div class="min-w-0 flex-1 space-y-5">
              <Input
                v-model="name"
                :label="$t('FLOWS.NAME')"
                :placeholder="$t('FLOWS.NAME_PLACEHOLDER')"
              />
              <div class="flex items-center gap-3">
                <Switch v-model="active" />
                <div class="min-w-0">
                  <p class="mb-0 text-heading-3 text-n-slate-12">
                    {{ $t('FLOWS.ACTIVE') }}
                  </p>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ $t('FLOWS.ACTIVE_HELP') }}
                  </p>
                </div>
              </div>
            </div>
            <div
              class="flex max-w-md flex-col gap-2 rounded-xl bg-n-alpha-2 p-4 text-sm text-n-slate-11"
            >
              <div class="flex items-start gap-2">
                <Icon
                  icon="i-lucide-lightbulb"
                  class="mt-0.5 size-4 shrink-0 text-n-amber-11"
                />
                <span>{{ $t('FLOWS.HINT_DESIGN') }}</span>
              </div>
              <div class="flex items-start gap-2 border-t border-n-weak pt-3">
                <Icon
                  icon="i-lucide-link-2"
                  class="mt-0.5 size-4 shrink-0 text-n-slate-10"
                />
                <span>{{ $t('FLOWS.INBOX_HINT') }}</span>
              </div>
            </div>
          </div>
        </CardLayout>

        <div class="flex flex-col gap-4">
          <div
            class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <div
              class="inline-flex rounded-xl bg-n-alpha-2 p-1 ring-1 ring-n-weak"
              role="tablist"
              :aria-label="$t('FLOWS.EDITOR_MODE')"
            >
              <button
                type="button"
                role="tab"
                :aria-selected="editorTab === 'builder'"
                class="rounded-lg px-4 py-2 text-sm font-medium transition-all"
                :class="tabClass('builder')"
                @click="setEditorTab('builder')"
              >
                {{ $t('FLOWS.TAB_BUILDER') }}
              </button>
              <button
                type="button"
                role="tab"
                :aria-selected="editorTab === 'json'"
                class="rounded-lg px-4 py-2 text-sm font-medium transition-all"
                :class="tabClass('json')"
                @click="setEditorTab('json')"
              >
                {{ $t('FLOWS.TAB_JSON') }}
              </button>
            </div>
            <p
              v-if="editorTab === 'builder'"
              class="mb-0 text-sm text-n-slate-11"
            >
              {{ $t('FLOWS.BUILDER_HELP') }}
            </p>
          </div>

          <div
            v-if="parseError && editorTab === 'json'"
            class="rounded-xl border border-n-ruby-8 bg-n-ruby-2/20 px-4 py-3 text-sm text-n-ruby-11"
          >
            <div class="flex items-start gap-2">
              <Icon
                icon="i-lucide-alert-circle"
                class="size-4 shrink-0 mt-0.5"
              />
              <div>
                <p class="mb-1 font-medium">
                  {{ $t('FLOWS.JSON_INVALID_BANNER') }}
                </p>
                <p class="mb-0 font-mono text-xs opacity-90">
                  {{ parseError }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-show="editorTab === 'builder'"
            class="flex flex-col gap-6 lg:flex-row lg:items-start"
          >
            <CardLayout layout="col" class="min-w-0 flex-1">
              <div
                class="flex items-center justify-between gap-4 border-b border-n-weak pb-4"
              >
                <div>
                  <h2 class="mb-1 text-base font-semibold text-n-slate-12">
                    {{ $t('FLOWS.FLOW_MAP_TITLE') }}
                  </h2>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ $t('FLOWS.BUILDER_HELP') }}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="faded"
                  :label="$t('FLOWS.EDIT_JSON_CTA')"
                  icon="i-lucide-code-2"
                  @click="setEditorTab('json')"
                />
              </div>
              <div class="pt-6">
                <FlowStepsList
                  v-model="definition"
                  :teams="teams"
                  :labels="labels"
                />
              </div>
            </CardLayout>
            <CardLayout
              layout="col"
              class="w-full shrink-0 lg:max-w-sm lg:sticky lg:top-4 lg:self-start"
            >
              <h3 class="mb-1 text-sm font-semibold text-n-slate-12">
                {{ $t('FLOWS.PREVIEW_TITLE') }}
              </h3>
              <p class="mb-4 text-xs text-n-slate-11">
                {{ $t('FLOWS.PREVIEW_SUBTITLE') }}
              </p>
              <FlowDefinitionTimeline :definition="parsedDefinition" />
            </CardLayout>
          </div>

          <CardLayout v-show="editorTab === 'json'" layout="col">
            <label
              class="mb-0 flex flex-col gap-2 text-heading-3 text-n-slate-12"
              for="flow-definition-json"
            >
              {{ $t('FLOWS.DEFINITION_JSON') }}
              <span class="text-sm font-normal text-n-slate-11">
                {{ $t('FLOWS.JSON_HELP') }}
              </span>
            </label>
            <textarea
              id="flow-definition-json"
              v-model="jsonText"
              rows="24"
              class="mt-2 w-full resize-y rounded-lg border border-n-weak bg-n-alpha-black2 px-4 py-3 font-mono text-sm leading-relaxed text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak transition-colors focus:outline-n-brand"
              spellcheck="false"
              autocomplete="off"
            />
          </CardLayout>

          <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div
              v-for="item in nodeReferenceCards"
              :key="item.icon"
              class="flex gap-3 rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3"
            >
              <Icon :icon="item.icon" class="size-5 shrink-0 text-n-slate-10" />
              <div class="min-w-0">
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ item.title }}
                </p>
                <p class="mb-0 text-xs text-n-slate-11">
                  {{ item.desc }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
